import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Upload, History } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { TransferImportDialog } from "@/components/TransferImportDialog";

const STATE = {
  active: ["Aktif", "bg-emerald-100 text-emerald-700"],
  expired: ["Süresi Dolmuş", "bg-amber-100 text-amber-700"],
  completed: ["Tamamlandı", "bg-blue-100 text-blue-700"],
  cancelled: ["İptal", "bg-red-100 text-red-700"],
  inactive: ["Pasif", "bg-slate-100 text-slate-600"],
  frozen: ["Dondurulmuş", "bg-cyan-100 text-cyan-700"],
};
const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "-");

const Stat = ({ label, value, tone, testId }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-4">
      <p className="text-xs text-grey">{label}</p>
      <p className={`text-xl font-bold mt-1 ${tone}`} data-testid={testId}>{value}</p>
    </CardContent>
  </Card>
);

export default function TransferPackagesReport({ businessId }) {
  const { formatPrice } = useCurrency();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/businesses/${businessId}/reports/transfer-packages`, { credentials: "include" });
    if (res.ok) setData(await res.json());
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) =>
    !q || `${r.member_name} ${r.member_no || ""} ${r.package_name} ${r.package_no || ""}`.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr"))
  );
  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="transfer-packages-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-violet-600" />
          <div>
            <h3 className="font-semibold text-navy">Devir Paketleri</h3>
            <p className="text-xs text-grey">Sistem öncesi satılıp aktarılan paketler. Önceden ödenen tutarlar gelir raporlarına dahil değildir.</p>
          </div>
        </div>
        <Button className="bg-violet-600 hover:bg-violet-700 rounded-xl" onClick={() => setShowImport(true)} data-testid="open-transfer-import-btn">
          <Upload className="w-4 h-4 mr-1" /> Toplu Devir Aktarımı
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Devir Paketi" value={`${s.count || 0} (${s.active_count || 0} aktif)`} tone="text-navy" testId="transfer-stat-count" />
        <Stat label="Kalan Ders" value={s.total_remaining_sessions || 0} tone="text-emerald-700" testId="transfer-stat-remaining" />
        <Stat label="Toplam Borç" value={formatPrice(s.total_debt || 0)} tone="text-red-600" testId="transfer-stat-debt" />
        <Stat label="Önceden Ödenen" value={formatPrice(s.total_prior_paid || 0)} tone="text-slate-700" testId="transfer-stat-prior-paid" />
        <Stat label="Sonradan Tahsil" value={formatPrice(s.total_paid_after || 0)} tone="text-violet-700" testId="transfer-stat-paid-after" />
      </div>

      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Üye, paket veya numara ara..." className="rounded-xl max-w-sm" data-testid="transfer-report-search" />

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-grey">
              <tr>
                <th className="p-3 text-left">Üye</th><th className="p-3 text-left">Paket</th>
                <th className="p-3 text-center">Devir Öncesi</th><th className="p-3 text-center">Sonra Yapılan</th>
                <th className="p-3 text-center">Kalan</th><th className="p-3 text-right">Fiyat</th>
                <th className="p-3 text-right">Borç</th><th className="p-3 text-left">Satış → Bitiş</th><th className="p-3 text-left">Durum</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const [label, cls] = STATE[r.state] || [r.state, "bg-slate-100"];
                return (
                  <tr key={r.mp_id} className="border-b hover:bg-slate-50/60 transition-colors" data-testid={`transfer-report-row-${r.mp_id}`}>
                    <td className="p-3"><p className="font-medium">{r.member_name}</p><p className="text-xs text-grey">{r.member_no || r.phone || ""}</p></td>
                    <td className="p-3">{r.package_name} {r.package_no && <span className="font-mono text-xs text-grey">· {r.package_no}</span>}</td>
                    <td className="p-3 text-center">{r.prior_used}</td>
                    <td className="p-3 text-center">{r.used_after}</td>
                    <td className="p-3 text-center font-semibold text-emerald-700">{r.remaining} / {r.total_sessions}</td>
                    <td className="p-3 text-right">{formatPrice(r.price)}</td>
                    <td className={`p-3 text-right ${r.debt > 0 ? "text-red-600 font-medium" : "text-grey"}`}>{formatPrice(r.debt)}</td>
                    <td className="p-3 whitespace-nowrap text-xs">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                    <td className="p-3"><Badge className={`${cls} text-xs`}>{label}</Badge></td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-grey" data-testid="transfer-report-empty">Henüz devir paketi yok.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <TransferImportDialog open={showImport} onOpenChange={setShowImport} businessId={businessId} onImported={load} />
    </div>
  );
}
